import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  Calendar,
  DollarSign,
  Zap,
  Settings,
  Info,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Sparkles,
  Target,
  Users,
  CalendarDays,
  Sun,
  Moon,
  PartyPopper,
  AlertCircle,
  Check,
  Plus,
  Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Price label colors
const LABEL_COLORS = {
  'Peak Pricing': 'bg-red-100 text-red-700',
  'High Demand': 'bg-orange-100 text-orange-700',
  'Event Premium': 'bg-purple-100 text-purple-700',
  'Standard Rate': 'bg-gray-100 text-gray-700',
  'Value Rate': 'bg-blue-100 text-blue-700',
  'Great Deal': 'bg-green-100 text-green-700',
};

export default function AIPricingPage() {
  const { user } = useAuth();
  const token = localStorage.getItem('ihostpms_token');
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  
  // Pricing data
  const [pricingCalendar, setPricingCalendar] = useState([]);
  const [insights, setInsights] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [config, setConfig] = useState(null);
  
  // Calendar navigation
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Dialogs
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDatePricing, setSelectedDatePricing] = useState(null);
  
  // Event form
  const [eventForm, setEventForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    impact_level: 'medium',
    location_city: '',
    category: ''
  });

  // A/B Experiments
  const [experiments, setExperiments] = useState([]);
  const [activeExperiment, setActiveExperiment] = useState(null);
  const [experimentInsights, setExperimentInsights] = useState(null);
  const [showExperimentDialog, setShowExperimentDialog] = useState(false);
  const [experimentForm, setExperimentForm] = useState({
    name: '',
    description: '',
    variants: [
      { name: 'Control', price: '' },
      { name: 'Test', price: '' }
    ],
    duration_days: 30
  });
  const [creatingExperiment, setCreatingExperiment] = useState(false);

  // Load properties
  useEffect(() => {
    const loadProperties = async () => {
      try {
        const resp = await fetch(`${BACKEND_URL}/api/properties`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          // Handle both array and {properties: [...]} response formats
          const propsList = Array.isArray(data) ? data : (data.properties || []);
          setProperties(propsList);
          if (propsList.length > 0) {
            setSelectedProperty(propsList[0]);
          }
        }
      } catch (err) {
        console.error('Failed to load properties:', err);
      } finally {
        setLoading(false);
      }
    };
    loadProperties();
  }, [token]);

  // Load pricing data when property or month changes
  const loadPricingData = useCallback(async () => {
    if (!selectedProperty) return;
    
    setCalculating(true);
    try {
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      // Load calendar
      const calendarResp = await fetch(
        `${BACKEND_URL}/api/ai-pricing/calendar/${selectedProperty.property_id}?` +
        `start_date=${startDate.toISOString().split('T')[0]}&` +
        `end_date=${endDate.toISOString().split('T')[0]}&` +
        `tenant_id=${user.tenant_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (calendarResp.ok) {
        const data = await calendarResp.json();
        setPricingCalendar(data.calendar || []);
      }
      
      // Load insights
      const insightsResp = await fetch(
        `${BACKEND_URL}/api/ai-pricing/insights/${selectedProperty.property_id}?tenant_id=${user.tenant_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (insightsResp.ok) {
        setInsights(await insightsResp.json());
      }
      
      // Load recommendations
      const recsResp = await fetch(
        `${BACKEND_URL}/api/ai-pricing/recommendations/${selectedProperty.property_id}?tenant_id=${user.tenant_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (recsResp.ok) {
        const data = await recsResp.json();
        setRecommendations(data.recommendations || []);
      }
      
      // Load config
      const configResp = await fetch(
        `${BACKEND_URL}/api/ai-pricing/config/${selectedProperty.property_id}?tenant_id=${user.tenant_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (configResp.ok) {
        setConfig(await configResp.json());
      }
      
    } catch (err) {
      console.error('Failed to load pricing data:', err);
    } finally {
      setCalculating(false);
    }
  }, [selectedProperty, currentMonth, token, user]);

  useEffect(() => {
    loadPricingData();
  }, [loadPricingData]);

  // Load experiments when property changes
  const loadExperiments = useCallback(async () => {
    if (!selectedProperty || !user?.tenant_id) return;
    
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/ai-pricing/experiments/${selectedProperty.property_id}?tenant_id=${user.tenant_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (resp.ok) {
        const data = await resp.json();
        setExperiments(data.experiments || []);
        
        // Find active experiment
        const active = (data.experiments || []).find(e => e.status === 'active');
        setActiveExperiment(active || null);
        
        // Load insights for active experiment
        if (active) {
          const insightsResp = await fetch(
            `${BACKEND_URL}/api/ai-pricing/experiment/${active.experiment_id}/insights?tenant_id=${user.tenant_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (insightsResp.ok) {
            setExperimentInsights(await insightsResp.json());
          }
        } else {
          setExperimentInsights(null);
        }
      }
    } catch (err) {
      console.error('Failed to load experiments:', err);
    }
  }, [selectedProperty, token, user]);

  useEffect(() => {
    loadExperiments();
  }, [loadExperiments]);

  // Create experiment
  const createExperiment = async () => {
    if (!selectedProperty || !experimentForm.name) return;
    
    // Validate variants
    const validVariants = experimentForm.variants.filter(v => v.name && v.price);
    if (validVariants.length < 2) {
      alert('Please add at least 2 variants with names and prices');
      return;
    }
    
    setCreatingExperiment(true);
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/ai-pricing/experiments/${selectedProperty.property_id}?tenant_id=${user.tenant_id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: experimentForm.name,
            description: experimentForm.description,
            variants: validVariants.map(v => ({ name: v.name, price: parseFloat(v.price) })),
            duration_days: experimentForm.duration_days
          })
        }
      );
      
      if (resp.ok) {
        setShowExperimentDialog(false);
        setExperimentForm({
          name: '',
          description: '',
          variants: [{ name: 'Control', price: '' }, { name: 'Test', price: '' }],
          duration_days: 30
        });
        loadExperiments();
      } else {
        const error = await resp.json();
        alert(error.detail || 'Failed to create experiment');
      }
    } catch (err) {
      console.error('Failed to create experiment:', err);
    } finally {
      setCreatingExperiment(false);
    }
  };

  // Update experiment status
  const updateExperimentStatus = async (experimentId, status) => {
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/ai-pricing/experiment/${experimentId}/status?tenant_id=${user.tenant_id}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status })
        }
      );
      
      if (resp.ok) {
        loadExperiments();
      }
    } catch (err) {
      console.error('Failed to update experiment status:', err);
    }
  };

  // Apply winner
  const applyExperimentWinner = async (experimentId) => {
    if (!confirm('This will update your property base price to the winning price. Continue?')) return;
    
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/ai-pricing/experiment/${experimentId}/apply-winner?tenant_id=${user.tenant_id}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (resp.ok) {
        const data = await resp.json();
        alert(`Success! New base price: ${selectedProperty?.currency || 'USD'} ${data.new_base_price}`);
        loadExperiments();
        loadPricingData();
      } else {
        const error = await resp.json();
        alert(error.detail || 'Failed to apply winner');
      }
    } catch (err) {
      console.error('Failed to apply winner:', err);
    }
  };

  // Delete experiment
  const deleteExperiment = async (experimentId) => {
    if (!confirm('Delete this experiment? This action cannot be undone.')) return;
    
    try {
      await fetch(
        `${BACKEND_URL}/api/ai-pricing/experiment/${experimentId}?tenant_id=${user.tenant_id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      loadExperiments();
    } catch (err) {
      console.error('Failed to delete experiment:', err);
    }
  };

  // Get detailed pricing for a specific date
  const loadDateDetails = async (date) => {
    if (!selectedProperty) return;
    
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/ai-pricing/calculate/${selectedProperty.property_id}?` +
        `date=${date}&tenant_id=${user.tenant_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (resp.ok) {
        const data = await resp.json();
        setSelectedDatePricing(data);
        setSelectedDate(date);
        setShowDetailDialog(true);
      }
    } catch (err) {
      console.error('Failed to load date details:', err);
    }
  };

  // Apply AI prices
  const applyAIPrices = async () => {
    if (!selectedProperty) return;
    
    const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/ai-pricing/apply/${selectedProperty.property_id}?` +
        `start_date=${startDate.toISOString().split('T')[0]}&` +
        `end_date=${endDate.toISOString().split('T')[0]}&` +
        `tenant_id=${user.tenant_id}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (resp.ok) {
        alert('AI prices applied successfully!');
        loadPricingData();
      }
    } catch (err) {
      console.error('Failed to apply prices:', err);
    }
  };

  // Save config
  const saveConfig = async () => {
    if (!selectedProperty || !config) return;
    
    try {
      await fetch(
        `${BACKEND_URL}/api/ai-pricing/config/${selectedProperty.property_id}?tenant_id=${user.tenant_id}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(config)
        }
      );
      setShowConfigDialog(false);
    } catch (err) {
      console.error('Failed to save config:', err);
    }
  };

  // Create event
  const createEvent = async () => {
    try {
      await fetch(
        `${BACKEND_URL}/api/ai-pricing/events?tenant_id=${user.tenant_id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(eventForm)
        }
      );
      setShowEventDialog(false);
      setEventForm({ name: '', start_date: '', end_date: '', impact_level: 'medium', location_city: selectedProperty?.location?.city || '', category: '' });
      loadPricingData();
    } catch (err) {
      console.error('Failed to create event:', err);
    }
  };

  // Render calendar
  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    
    // Empty cells for days before first of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-gray-50" />);
    }
    
    // Day cells
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const pricing = pricingCalendar.find(p => p.date === dateStr);
      const isToday = new Date().toISOString().split('T')[0] === dateStr;
      
      days.push(
        <div
          key={day}
          onClick={() => loadDateDetails(dateStr)}
          className={`h-24 p-2 border border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors
            ${isToday ? 'ring-2 ring-blue-500 ring-inset' : ''}
          `}
        >
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
              {day}
            </span>
            {pricing?.has_event && (
              <PartyPopper className="w-3.5 h-3.5 text-purple-500" />
            )}
          </div>
          
          {pricing && (
            <div className="mt-1">
              <div className="text-lg font-bold text-gray-900">
                {selectedProperty?.currency || 'KES'} {pricing.price?.toLocaleString()}
              </div>
              <div className={`text-xs px-1.5 py-0.5 rounded-full inline-block mt-1 ${LABEL_COLORS[pricing.label] || 'bg-gray-100 text-gray-600'}`}>
                {pricing.adjustment_percent > 0 ? '+' : ''}{pricing.adjustment_percent}%
              </div>
            </div>
          )}
        </div>
      );
    }
    
    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-pricing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="w-7 h-7 text-purple-600" />
            AI Dynamic Pricing
          </h1>
          <p className="text-gray-500 mt-1">
            Optimize your rates with AI-powered recommendations
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select
            value={selectedProperty?.property_id || ''}
            onValueChange={(val) => setSelectedProperty(properties.find(p => p.property_id === val))}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select property" />
            </SelectTrigger>
            <SelectContent>
              {properties.map(p => (
                <SelectItem key={p.property_id} value={p.property_id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={() => setShowConfigDialog(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">ML Confidence</p>
                <p className="text-xl font-bold text-gray-900">
                  {insights?.optimization?.potential_increase !== null ? 'High' : 'Building...'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Revenue Potential</p>
                <p className="text-xl font-bold text-gray-900">
                  {insights?.optimization?.potential_increase 
                    ? `+${insights.optimization.potential_increase}%` 
                    : 'Optimal'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Competitors</p>
                <p className="text-xl font-bold text-gray-900">
                  {insights?.competitor_analysis?.competitor_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Opportunities</p>
                <p className="text-xl font-bold text-gray-900">
                  {recommendations.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="calendar" className="space-y-4">
        <TabsList>
          <TabsTrigger value="calendar">
            <Calendar className="w-4 h-4 mr-2" />
            Pricing Calendar
          </TabsTrigger>
          <TabsTrigger value="recommendations">
            <Sparkles className="w-4 h-4 mr-2" />
            Recommendations
          </TabsTrigger>
          <TabsTrigger value="competitors">
            <Target className="w-4 h-4 mr-2" />
            Market Analysis
          </TabsTrigger>
          <TabsTrigger value="events">
            <PartyPopper className="w-4 h-4 mr-2" />
            Local Events
          </TabsTrigger>
          <TabsTrigger value="experiments">
            <TrendingUp className="w-4 h-4 mr-2" />
            A/B Experiments
            {activeExperiment && (
              <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700">Live</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Calendar Tab */}
        <TabsContent value="calendar">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </CardTitle>
                <CardDescription>
                  Click any date to see detailed pricing breakdown
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMonth(new Date())}
                >
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button onClick={applyAIPrices} className="ml-4 bg-purple-600 hover:bg-purple-700">
                  <Zap className="w-4 h-4 mr-2" />
                  Apply AI Prices
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {calculating ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                  <span className="ml-2 text-gray-500">Calculating optimal prices...</span>
                </div>
              ) : (
                <>
                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-0 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-0 border border-gray-200 rounded-lg overflow-hidden">
                    {renderCalendar()}
                  </div>
                  {/* Legend */}
                  <div className="flex flex-wrap gap-3 mt-4">
                    {Object.entries(LABEL_COLORS).map(([label, color]) => (
                      <div key={label} className="flex items-center gap-1.5">
                        <div className={`w-3 h-3 rounded-full ${color.split(' ')[0]}`} />
                        <span className="text-xs text-gray-600">{label}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations">
          <Card>
            <CardHeader>
              <CardTitle>AI Recommendations</CardTitle>
              <CardDescription>
                Actionable suggestions to optimize your revenue
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recommendations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No recommendations at this time</p>
                  <p className="text-sm">Your pricing looks optimized!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recommendations.map((rec, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border ${
                        rec.type === 'event' ? 'bg-purple-50 border-purple-200' :
                        rec.type === 'high_demand' ? 'bg-orange-50 border-orange-200' :
                        rec.type === 'gap_night' ? 'bg-blue-50 border-blue-200' :
                        'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {rec.type === 'event' && <PartyPopper className="w-5 h-5 text-purple-600 mt-0.5" />}
                          {rec.type === 'high_demand' && <TrendingUp className="w-5 h-5 text-orange-600 mt-0.5" />}
                          {rec.type === 'gap_night' && <CalendarDays className="w-5 h-5 text-blue-600 mt-0.5" />}
                          <div>
                            <p className="font-medium text-gray-900">{rec.message}</p>
                            {rec.suggested_price && (
                              <p className="text-sm text-gray-600 mt-1">
                                Suggested: {selectedProperty?.currency || 'KES'} {rec.suggested_price.toLocaleString()}
                              </p>
                            )}
                            {rec.suggested_discount && (
                              <p className="text-sm text-gray-600 mt-1">
                                Suggested discount: {Math.round(rec.suggested_discount * 100)}%
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge variant="outline">{rec.date}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Competitors Tab */}
        <TabsContent value="competitors">
          <Card>
            <CardHeader>
              <CardTitle>Market Analysis</CardTitle>
              <CardDescription>
                How your pricing compares to similar properties
              </CardDescription>
            </CardHeader>
            <CardContent>
              {insights?.competitor_analysis?.has_competitors ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Your Base Price</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedProperty?.currency || 'KES'} {selectedProperty?.base_price?.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-500">Market Average</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {selectedProperty?.currency || 'KES'} {Math.round(insights.competitor_analysis.avg_price).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-500">Market Min</p>
                      <p className="text-2xl font-bold text-green-700">
                        {selectedProperty?.currency || 'KES'} {insights.competitor_analysis.min_price?.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-sm text-gray-500">Market Max</p>
                      <p className="text-2xl font-bold text-red-700">
                        {selectedProperty?.currency || 'KES'} {insights.competitor_analysis.max_price?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  {insights.optimization?.recommendation && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">AI Recommendation</p>
                          <p className="text-sm text-amber-700 mt-1">{insights.optimization.recommendation}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Similar Properties</h4>
                    <div className="space-y-2">
                      {insights.competitor_analysis.competitors?.map((comp, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-700">{comp.name}</span>
                          <div className="flex items-center gap-3">
                            {comp.average_rating && (
                              <span className="text-sm text-gray-500">⭐ {comp.average_rating.toFixed(1)}</span>
                            )}
                            <span className="font-medium">
                              {selectedProperty?.currency || 'KES'} {comp.base_price?.toLocaleString()}/night
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No competitor data available</p>
                  <p className="text-sm">We need more properties in your area for comparison</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Local Events</CardTitle>
                <CardDescription>
                  Events that impact pricing in your area
                </CardDescription>
              </div>
              <Button onClick={() => {
                setEventForm({...eventForm, location_city: selectedProperty?.location?.city || ''});
                setShowEventDialog(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Event
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-4">
                Add local events (conferences, festivals, holidays) to automatically adjust pricing during high-demand periods.
              </p>
              
              {/* Event list would go here - loaded from API */}
              <div className="text-center py-8 text-gray-500">
                <PartyPopper className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No events added yet</p>
                <p className="text-sm">Add local events to boost revenue during peak times</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* A/B Experiments Tab */}
        <TabsContent value="experiments">
          <div className="space-y-6">
            {/* Tip Banner */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-blue-900">What is A/B Price Testing?</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Test different prices with real guests to find your optimal rate. The system randomly shows 
                    different prices and tracks which one gets more bookings. After enough data, you'll know 
                    exactly which price maximizes your revenue.
                  </p>
                </div>
              </div>
            </div>

            {/* Active Experiment */}
            {activeExperiment ? (
              <Card className="border-green-200 bg-green-50/30">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle>{activeExperiment.name}</CardTitle>
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    </div>
                    <CardDescription>
                      {activeExperiment.description || 'Testing different price points'}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => updateExperimentStatus(activeExperiment.experiment_id, 'paused')}
                    >
                      Pause
                    </Button>
                    {experimentInsights?.winner && experimentInsights?.statistical_significance >= 95 && (
                      <Button 
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => applyExperimentWinner(activeExperiment.experiment_id)}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Apply Winner
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Variants Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium">Variant</th>
                          <th className="text-right py-2 font-medium">Price</th>
                          <th className="text-right py-2 font-medium">Views</th>
                          <th className="text-right py-2 font-medium">Bookings</th>
                          <th className="text-right py-2 font-medium">Conversion</th>
                          <th className="text-right py-2 font-medium">Revenue/View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeExperiment.variants?.map((variant, idx) => (
                          <tr key={idx} className={`border-b ${variant.variant_id === activeExperiment.winner ? 'bg-green-100' : ''}`}>
                            <td className="py-2">
                              <div className="flex items-center gap-2">
                                {variant.name}
                                {variant.variant_id === activeExperiment.winner && (
                                  <Badge className="bg-green-600 text-white text-xs">Winner</Badge>
                                )}
                              </div>
                            </td>
                            <td className="text-right py-2 font-medium">
                              {selectedProperty?.currency || 'USD'} {variant.price?.toLocaleString()}
                            </td>
                            <td className="text-right py-2">{variant.views || 0}</td>
                            <td className="text-right py-2">{variant.bookings || 0}</td>
                            <td className="text-right py-2">{variant.conversion_rate?.toFixed(2) || '0.00'}%</td>
                            <td className="text-right py-2">${variant.revenue_per_view?.toFixed(2) || '0.00'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Stats Summary */}
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{activeExperiment.total_views || 0}</p>
                      <p className="text-sm text-gray-500">Total Views</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{activeExperiment.total_bookings || 0}</p>
                      <p className="text-sm text-gray-500">Total Bookings</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className={`text-2xl font-bold ${activeExperiment.statistical_significance >= 95 ? 'text-green-600' : 'text-gray-900'}`}>
                        {activeExperiment.statistical_significance || 0}%
                      </p>
                      <p className="text-sm text-gray-500">Confidence</p>
                    </div>
                  </div>

                  {/* Insights */}
                  {experimentInsights?.insights?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <h4 className="font-medium text-gray-900">AI Insights</h4>
                      {experimentInsights.insights.map((insight, idx) => (
                        <div key={idx} className={`p-3 rounded-lg flex items-start gap-2 ${
                          insight.impact === 'positive' ? 'bg-green-50 border border-green-200' :
                          insight.impact === 'negative' ? 'bg-red-50 border border-red-200' :
                          'bg-gray-50 border border-gray-200'
                        }`}>
                          {insight.impact === 'positive' ? (
                            <TrendingUp className="w-4 h-4 text-green-600 mt-0.5" />
                          ) : insight.impact === 'negative' ? (
                            <TrendingDown className="w-4 h-4 text-red-600 mt-0.5" />
                          ) : (
                            <Info className="w-4 h-4 text-gray-600 mt-0.5" />
                          )}
                          <p className="text-sm">{insight.message}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Recommendation */}
                  {experimentInsights?.recommendation && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-900">Recommendation</p>
                          <p className="text-sm text-amber-700">{experimentInsights.recommendation.message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tips */}
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                    <p className="text-xs text-blue-700">
                      <strong>Tip:</strong> Wait for at least 100 views and 95% confidence before making decisions. 
                      Results below this threshold may change significantly with more data.
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>A/B Price Testing</CardTitle>
                    <CardDescription>
                      Find your optimal price with data-driven experiments
                    </CardDescription>
                  </div>
                  <Button onClick={() => {
                    setExperimentForm({
                      name: '',
                      description: '',
                      variants: [
                        { name: 'Control', price: selectedProperty?.base_price || '' },
                        { name: 'Test', price: '' }
                      ],
                      duration_days: 30
                    });
                    setShowExperimentDialog(true);
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Experiment
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">No active experiment</p>
                    <p className="text-sm text-gray-400 mt-1">Create an experiment to start testing different prices</p>
                  </div>

                  {/* Quick Tips */}
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                        <span className="text-blue-600 font-bold">1</span>
                      </div>
                      <h4 className="font-medium text-gray-900">Set Up Variants</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Create 2-4 price points to test. Example: $140, $150, $160
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                        <span className="text-blue-600 font-bold">2</span>
                      </div>
                      <h4 className="font-medium text-gray-900">Gather Data</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Guests are randomly shown different prices. Track views and bookings.
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                        <span className="text-blue-600 font-bold">3</span>
                      </div>
                      <h4 className="font-medium text-gray-900">Apply Winner</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Once results are significant (95%+), apply the winning price.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Past Experiments */}
            {experiments.filter(e => e.status !== 'active').length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Past Experiments</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {experiments.filter(e => e.status !== 'active').map(exp => (
                      <div key={exp.experiment_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{exp.name}</span>
                            <Badge variant="outline" className={
                              exp.status === 'completed' ? 'border-green-300 text-green-700' :
                              exp.status === 'paused' ? 'border-amber-300 text-amber-700' :
                              'border-gray-300 text-gray-700'
                            }>
                              {exp.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500">
                            {exp.total_views} views · {exp.total_bookings} bookings · 
                            {exp.winner ? ' Winner found' : ' No winner'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {exp.status === 'paused' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => updateExperimentStatus(exp.experiment_id, 'active')}
                            >
                              Resume
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => deleteExperiment(exp.experiment_id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Experiment Dialog */}
      <Dialog open={showExperimentDialog} onOpenChange={setShowExperimentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create A/B Pricing Experiment</DialogTitle>
            <DialogDescription>
              Test different prices to find what works best for your property
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Experiment Name</Label>
              <Input
                value={experimentForm.name}
                onChange={(e) => setExperimentForm({...experimentForm, name: e.target.value})}
                placeholder="e.g., Winter Price Test"
              />
            </div>
            
            <div>
              <Label>Description (Optional)</Label>
              <Input
                value={experimentForm.description}
                onChange={(e) => setExperimentForm({...experimentForm, description: e.target.value})}
                placeholder="Testing if lower prices increase bookings"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Price Variants</Label>
                {experimentForm.variants.length < 4 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setExperimentForm({
                      ...experimentForm,
                      variants: [...experimentForm.variants, { name: `Variant ${String.fromCharCode(65 + experimentForm.variants.length)}`, price: '' }]
                    })}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {experimentForm.variants.map((variant, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      placeholder="Name"
                      value={variant.name}
                      onChange={(e) => {
                        const updated = [...experimentForm.variants];
                        updated[idx].name = e.target.value;
                        setExperimentForm({...experimentForm, variants: updated});
                      }}
                      className="w-1/3"
                    />
                    <Input
                      type="number"
                      placeholder="Price"
                      value={variant.price}
                      onChange={(e) => {
                        const updated = [...experimentForm.variants];
                        updated[idx].price = e.target.value;
                        setExperimentForm({...experimentForm, variants: updated});
                      }}
                      className="w-1/3"
                    />
                    {experimentForm.variants.length > 2 && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          const updated = experimentForm.variants.filter((_, i) => i !== idx);
                          setExperimentForm({...experimentForm, variants: updated});
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Traffic will be split equally between variants
              </p>
            </div>
            
            <div>
              <Label>Duration (Days)</Label>
              <Select
                value={String(experimentForm.duration_days)}
                onValueChange={(val) => setExperimentForm({...experimentForm, duration_days: parseInt(val)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days (recommended)</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Longer experiments give more reliable results
              </p>
            </div>

            {/* Tip */}
            <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="text-xs text-amber-700">
                <strong>Pro tip:</strong> Start with small price differences ($10-20) to minimize risk. 
                You can always run another experiment with bigger differences if results are unclear.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExperimentDialog(false)}>Cancel</Button>
            <Button onClick={createExperiment} disabled={creatingExperiment}>
              {creatingExperiment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Experiment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>AI Pricing Settings</DialogTitle>
            <DialogDescription>
              Configure how AI calculates your recommended prices
            </DialogDescription>
          </DialogHeader>
          
          {config && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable AI Pricing</Label>
                  <p className="text-sm text-gray-500">Show AI recommendations</p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(val) => setConfig({...config, enabled: val})}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Apply Prices</Label>
                  <p className="text-sm text-gray-500">Automatically update rates</p>
                </div>
                <Switch
                  checked={config.auto_apply}
                  onCheckedChange={(val) => setConfig({...config, auto_apply: val})}
                />
              </div>
              
              <div className="border-t pt-4">
                <Label className="text-sm font-medium">Pricing Factors</Label>
                <div className="space-y-3 mt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Seasonal Adjustments</span>
                    <Switch
                      checked={config.seasonal_enabled}
                      onCheckedChange={(val) => setConfig({...config, seasonal_enabled: val})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Day-of-Week Pricing</span>
                    <Switch
                      checked={config.dow_enabled}
                      onCheckedChange={(val) => setConfig({...config, dow_enabled: val})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Occupancy-Based</span>
                    <Switch
                      checked={config.occupancy_enabled}
                      onCheckedChange={(val) => setConfig({...config, occupancy_enabled: val})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Event Pricing</span>
                    <Switch
                      checked={config.events_enabled}
                      onCheckedChange={(val) => setConfig({...config, events_enabled: val})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">ML Recommendations</span>
                    <Switch
                      checked={config.ml_enabled}
                      onCheckedChange={(val) => setConfig({...config, ml_enabled: val})}
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 border-t pt-4">
                <div>
                  <Label>Min Price</Label>
                  <Input
                    type="number"
                    value={config.min_price || ''}
                    onChange={(e) => setConfig({...config, min_price: e.target.value ? parseFloat(e.target.value) : null})}
                    placeholder="No minimum"
                  />
                </div>
                <div>
                  <Label>Max Price</Label>
                  <Input
                    type="number"
                    value={config.max_price || ''}
                    onChange={(e) => setConfig({...config, max_price: e.target.value ? parseFloat(e.target.value) : null})}
                    placeholder="No maximum"
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancel</Button>
            <Button onClick={saveConfig}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Local Event</DialogTitle>
            <DialogDescription>
              Events will automatically increase prices during the specified dates
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Event Name</Label>
              <Input
                value={eventForm.name}
                onChange={(e) => setEventForm({...eventForm, name: e.target.value})}
                placeholder="e.g., Safari Rally, Tech Conference"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm({...eventForm, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Impact Level</Label>
              <Select
                value={eventForm.impact_level}
                onValueChange={(val) => setEventForm({...eventForm, impact_level: val})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="major">Major (+50% price)</SelectItem>
                  <SelectItem value="high">High (+35% price)</SelectItem>
                  <SelectItem value="medium">Medium (+20% price)</SelectItem>
                  <SelectItem value="low">Low (+10% price)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>City</Label>
              <Input
                value={eventForm.location_city}
                onChange={(e) => setEventForm({...eventForm, location_city: e.target.value})}
                placeholder="e.g., Nairobi"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)}>Cancel</Button>
            <Button onClick={createEvent}>Add Event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Pricing Breakdown - {selectedDate && new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDatePricing && (
            <div className="space-y-4 py-4">
              {/* Final Price */}
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-600">AI Recommended Price</p>
                <p className="text-3xl font-bold text-purple-700">
                  {selectedProperty?.currency || 'KES'} {selectedDatePricing.recommended_price?.toLocaleString()}
                </p>
                <Badge className={LABEL_COLORS[selectedDatePricing.price_label]}>
                  {selectedDatePricing.price_label}
                </Badge>
              </div>
              
              {/* Base Price */}
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-600">Base Price</span>
                <span className="font-medium">{selectedProperty?.currency || 'KES'} {selectedDatePricing.base_price?.toLocaleString()}</span>
              </div>
              
              {/* Phase 1: Basic Factors */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <Sun className="w-4 h-4" /> Basic Demand Factors
                </h4>
                
                <div className="flex justify-between text-sm py-1">
                  <span className="text-gray-600">{selectedDatePricing.phase1_basic?.seasonal?.label}</span>
                  <span className={selectedDatePricing.phase1_basic?.seasonal?.multiplier > 1 ? 'text-green-600' : 'text-red-600'}>
                    {selectedDatePricing.phase1_basic?.seasonal?.multiplier > 1 ? '+' : ''}
                    {Math.round((selectedDatePricing.phase1_basic?.seasonal?.multiplier - 1) * 100)}%
                  </span>
                </div>
                
                <div className="flex justify-between text-sm py-1">
                  <span className="text-gray-600">{selectedDatePricing.phase1_basic?.day_of_week?.label}</span>
                  <span className={selectedDatePricing.phase1_basic?.day_of_week?.multiplier > 1 ? 'text-green-600' : 'text-red-600'}>
                    {selectedDatePricing.phase1_basic?.day_of_week?.multiplier > 1 ? '+' : ''}
                    {Math.round((selectedDatePricing.phase1_basic?.day_of_week?.multiplier - 1) * 100)}%
                  </span>
                </div>
                
                <div className="flex justify-between text-sm py-1">
                  <span className="text-gray-600">
                    Occupancy ({selectedDatePricing.phase1_basic?.occupancy?.rate}%)
                  </span>
                  <span className={selectedDatePricing.phase1_basic?.occupancy?.multiplier > 1 ? 'text-green-600' : 'text-red-600'}>
                    {selectedDatePricing.phase1_basic?.occupancy?.multiplier > 1 ? '+' : ''}
                    {Math.round((selectedDatePricing.phase1_basic?.occupancy?.multiplier - 1) * 100)}%
                  </span>
                </div>
                
                {selectedDatePricing.phase1_basic?.gap_night?.is_gap && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-gray-600">Gap Night Discount</span>
                    <span className="text-red-600">
                      {Math.round((selectedDatePricing.phase1_basic?.gap_night?.multiplier - 1) * 100)}%
                    </span>
                  </div>
                )}
              </div>
              
              {/* Phase 2: Events */}
              {selectedDatePricing.phase2_events?.has_events && (
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <PartyPopper className="w-4 h-4" /> Event Impact
                  </h4>
                  {selectedDatePricing.phase2_events.events?.map((evt, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1">
                      <span className="text-gray-600">{evt.name}</span>
                      <span className="text-purple-600">
                        +{Math.round((evt.multiplier - 1) * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Phase 3: ML */}
              {selectedDatePricing.phase3_ml && (
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Brain className="w-4 h-4" /> ML Analysis
                  </h4>
                  <div className="text-sm text-gray-600">
                    <p>Confidence: {selectedDatePricing.phase3_ml.confidence_label} ({Math.round(selectedDatePricing.phase3_ml.confidence_score * 100)}%)</p>
                    {selectedDatePricing.phase3_ml.recommendations?.map((rec, idx) => (
                      <p key={idx} className="mt-1">• {rec}</p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Total Adjustment */}
              <div className="flex justify-between items-center py-2 border-t font-medium">
                <span>Total Adjustment</span>
                <span className={selectedDatePricing.total_adjustment_percent > 0 ? 'text-green-600' : 'text-red-600'}>
                  {selectedDatePricing.total_adjustment_percent > 0 ? '+' : ''}
                  {selectedDatePricing.total_adjustment_percent}%
                </span>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
